import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { Content } from 'app/constants';
import { SidebarList } from 'app/models';
import {
  deleteOutlet,
  loadOutletProfile,
  resetAuthorProfile,
  resetSearch,
  resetTopicProfile,
  setCoverages,
  setFacetsSearch,
  setInputSearchPlaceholder,
  setOutletDialog,
  setSearch,
  showOutletManagerDialog,
  startLoading,
  tabControl,
  headerMenuActive,
  loadAuthorsOutlets,
} from 'app/redux/actions';
import { OutletService, SearchFilterDefaultsService } from 'app/services';
import { eq } from 'lodash';
import { ConfirmationService } from 'primeng/api';
import { ToastNotificationService } from 'app/components';

@Component({
  selector: 'app-sidebar-outlet-list',
  template: `
    <app-sidebar-entity-table
      kind="outlet"
      [items]="outlets"
      [allItems]="allOutlets"
      (selected)="selectedItem($event)"
      (edit)="openEditOutletManagerDialog($event)"
    ></app-sidebar-entity-table>
  `,
})
export class SidebarOutletListComponent implements OnInit {
  public outlets: SidebarList[];
  public allOutlets: SidebarList[];
  public selectItem: any;
  public sort: any;
  public showScrollbar: boolean;
  public projectId: number;
  public loading: boolean;
  public loaded: boolean;
  public currentProject: any;
  public keywords: string;
  public isEdit: boolean;
  public isUnfollow: boolean;
  public parentName: string;
  public reordering: boolean;

  @ViewChildren('sortNameIcon, sortCategoryIcon', {
    read: ElementRef,
  })
  sortIcons: QueryList<ElementRef>;

  constructor(
    private store: Store<AppState>,
    private confirmationService: ConfirmationService,
    private outletService: OutletService,
    private notificationService: ToastNotificationService,
    private searchFilterDefaults: SearchFilterDefaultsService
  ) {
    this.isEdit = false;
    this.isUnfollow = false;
    this.keywords = null;
    this.showScrollbar = false;
    this.loading = false;
    this.loaded = false;
    this.outlets = [];
    this.allOutlets = [];
    this.parentName = Content.Outlet;
    this.reordering = false;
  }

  ngOnInit() {
    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        const { keywords } = searchBody;
        this.keywords = keywords;
      }
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.currentProject = projectSelected;
      }
    });

    this.store
      .select('authorsOutletsList')
      .subscribe(({ outlets, loading, loaded, errors }) => {
        this.loading = loading;
        this.loaded = loaded;
        if (outlets && outlets.length > 0 && loaded) {
          this.allOutlets = outlets.map(
            (item) =>
              new SidebarList({
                title: item.name,
                subTitle: item.categoryName || item.label?.name,
                picture: this.loadPicture(item.partyId),
                hasPicture: true,
                parentObject: {
                  ...item,
                  categoryName: item.categoryName || item.label?.name,
                  categoryId: item.label?.id,
                  name: item.name,
                  id: item.id,
                  partyId: item.partyId,
                },
                parentName: this.parentName,
              })
          );
          const visibleOutlets = this.allOutlets.filter(
            (item) => item.subTitle !== 'Ignore'
          );
          this.outlets = [
            ...visibleOutlets.filter((item) => !!item.subTitle),
            ...visibleOutlets.filter((item) => !item.subTitle),
          ];
        } else {
          this.allOutlets = [];
          this.outlets = [];
        }
      });

    this.store
      .select('outletDialog')
      .subscribe(({ outletId, loading, loaded }) => {
        if (outletId) {
          this.loadingOutletDialog(outletId, loading, loaded);
        }
      });
  }

  public selectedItem(item) {
    // Wait for the sidebar's close transition before starting the search UI.
    window.setTimeout(() => this.searchForOutlet(item), 250);
  }

  private searchForOutlet(item) {
    this.store.dispatch(resetSearch({ onReset: true }));

    // TODO: Open coverage section
    this.store.dispatch(tabControl({ index: 1 }));
    this.store.dispatch(headerMenuActive({ menuActive: 'Home' }));

    // TODO: Hide author/topic profile
    this.store.dispatch(resetAuthorProfile());
    this.store.dispatch(resetTopicProfile());

    // TODO: Show author profile
    this.store.dispatch(
      loadOutletProfile({
        outletId: item.partyId,
      })
    );

    // TODO: Run search to content items on converage section
    this.store.dispatch(
      setCoverages({
        searchBody: this.searchFilterDefaults.createSearch({
          keywords: this.keywords,
          outlets: [item.partyId],
          authors: [],
        }),
        topicId: null,
        sort: 'recent',
        page: 0,
        size: 50,
        isSearch: true,
      })
    );

    const search = this.searchFilterDefaults.createSearch({
      keywords: null,
      facets: { AUTHORS: null, COMPANIES: null, TOPICS: null },
      outlets: [item.partyId],
      authors: [],
    });

    // TODO: Show facets table
    this.store.dispatch(
      setFacetsSearch({
        searchBody: search,
        page: 0,
        isNewSearch: true,
      })
    );
    this.store.dispatch(
      setSearch({
        search: search,
      })
    );

    this.store.dispatch(
      setInputSearchPlaceholder({ placeholder: `${item.name} (Outlet)` })
    );

    // Clear the stale reset flag set at the top of this method so a freshly
    // mounted <app-search> (e.g. first click from the Report screen) does not
    // read onReset:true and undo the tabControl above.
    this.store.dispatch(resetSearch({ onReset: false }));

    // this.store.dispatch(
    //   setOutletDialog({
    //     outletId: item.partyId,
    //     projectSelected: this.currentProject,
    //     keywords: this.keywords,
    //     sort: 'recent'
    //   })
    // );
  }

  public reorderOutlets(reordered: SidebarList[]) {
    const previous = this.outlets;
    this.outlets = reordered;
    this.reordering = true;

    const reorderedItems = [...reordered];
    const completeOrder = this.allOutlets.map((item) =>
      item.subTitle !== 'Ignore' ? reorderedItems.shift() : item
    );
    const orderedIds = completeOrder.map((item) => item.parentObject.id);
    this.outletService
      .updateOutletOrder(this.currentProject.value, orderedIds)
      .subscribe(
        () => {
          this.reordering = false;
          this.store.dispatch(
            loadAuthorsOutlets({ projectId: this.currentProject.value })
          );
        },
        () => {
          this.outlets = previous;
          this.reordering = false;
          this.notificationService.showMessage(
            'Unable to save outlet order.',
            'Error'
          );
        }
      );
  }

  loadingOutletDialog(outletId, loading, loaded) {
    const index = this.outlets.findIndex(({ parentObject }) =>
      eq(parentObject.partyId, outletId)
    );
    if (index < 0) {
      return;
    }
    if (loading) {
      this.outlets[index].loadingIcon = true;
    }
    if (loaded) {
      this.outlets[index].loadingIcon = false;
    }
  }

  openCreateOutletManagerDialog() {
    this.store.dispatch(showOutletManagerDialog({ outlet: null }));
  }

  openEditOutletManagerDialog(outlet) {
    this.store.dispatch(showOutletManagerDialog({ outlet }));
  }

  handleUnfollow(value) {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to unfollow this outlet?',
      header: 'Unfollow Confirmation',
      icon: 'pi pi-exclamation-triangle',
      key: 'unfollowConfirmation',
      accept: () => {
        this.unfollow(value);
      },
      reject: (type) => {
        // handle rejected option ConfirmEventType.CANCEL
      },
    });
  }

  public unfollow(value) {
    const payload = {
      userProject: this.currentProject,
      id: value.partyId,
      name: value.name,
    };
    this.store.dispatch(
      deleteOutlet({
        payload,
      })
    );
  }

  public loadPicture(id) {
    return this.outletService.getOutletPicture(id);
  }
}
